import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    darken,
    lighten,
    PaletteColor,
    Typography,
    useTheme,
} from '@mui/material';
import React from 'react';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { Offer, useOffer } from '../../hooks';

export type SelectOfferContentProps = {
    offers: Array<Offer>;
    selectedOfferId?: number;
    onSelectOffer: (offer?: Offer) => void;
};

const resolvePaletteColor = (
    colorName: string,
    palette: { error: PaletteColor; primary: PaletteColor },
): PaletteColor => {
    if (colorName === 'default' || colorName === 'danger') {
        return palette.error;
    }
    return palette.primary;
};

const SelectOfferContent: React.FunctionComponent<SelectOfferContentProps> = function (props) {
    const { palette } = useTheme();
    const { offers = [], selectedOfferId, onSelectOffer } = props;
    const { getOfferTypeColor, getOfferTypeLabel } = useOffer();

    const bgSelected = (color?: PaletteColor) => {
        if (!color) {
            return palette.background.default;
        }

        return palette.mode === 'light' ? lighten(color.main, 0.85) : darken(color.main, 0.6);
    };

    const avatarBg = palette.mode === 'light' ? palette.grey[100] : palette.grey[800];
    const avatarIconColor = palette.mode === 'light' ? palette.grey[600] : palette.grey[400];

    return (
        <Box
            sx={{
                marginTop: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '1em 0',
                minWidth: '500px',
            }}
        >
            <Typography variant="l2dh6">Sélection d&apos;une offre</Typography>
            <Card
                variant="outlined"
                data-testid="no-offer-choice"
                sx={{
                    margin: 0,
                    cursor: 'pointer',
                    bgcolor: !selectedOfferId ? bgSelected() : palette.background.paper,
                    border: !selectedOfferId ? 2 : 1,
                    borderColor: !selectedOfferId ? palette.primary.main : palette.divider,
                    '&:hover': { boxShadow: 2 },
                }}
                onClick={() => onSelectOffer(undefined)}
            >
                <CardContent sx={{ paddingTop: 0, paddingBottom: '0px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: avatarBg }}>
                            <FaOptionIcon
                                icon="times"
                                sx={{ color: avatarIconColor }}
                            />
                        </Avatar>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5em',
                                alignItems: 'flex-start',
                                flexGrow: 1,
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                fontWeight="500"
                            >
                                Aucune offre
                            </Typography>
                            <Typography
                                variant="body2"
                                color={palette.text.secondary}
                            >
                                Ne pas configurer d&lsquo;offre pour ce domaine
                            </Typography>
                        </Box>
                        {!selectedOfferId && (
                            <FaOptionIcon
                                icon="check"
                                sx={{ color: palette.primary.main }}
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>
            <Typography
                variant="subtitle2"
                color={palette.text.secondary}
                gutterBottom
            >
                Offres disponibles
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1em 0' }}>
                {offers.map((offer) => {
                    const colorName = getOfferTypeColor(offer);
                    const color = resolvePaletteColor(colorName, palette);
                    const isSelected = selectedOfferId === offer.id;
                    const chipBg =
                        palette.mode === 'light'
                            ? lighten(color.light, 0.8)
                            : darken(color.main, 0.6);
                    const chipText = palette.mode === 'light' ? color.dark : color.light;

                    return (
                        <Card
                            key={offer.id}
                            variant="outlined"
                            data-testid="offer-choice"
                            sx={{
                                margin: 0,
                                cursor: 'pointer',
                                bgcolor: isSelected ? bgSelected(color) : palette.background.paper,
                                border: isSelected ? 2 : 1,
                                borderColor: isSelected ? color.main : palette.divider,
                                '&:hover': { boxShadow: 2 },
                            }}
                            onClick={() => onSelectOffer(offer)}
                        >
                            <CardContent sx={{ paddingTop: 0, paddingBottom: '0px !important' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {offer.icon && (
                                        <Avatar
                                            sx={{
                                                backgroundColor: chipBg,
                                            }}
                                        >
                                            <FaOptionIcon
                                                icon={['fasl', offer.icon as IconName]}
                                                sx={{ color: chipText }}
                                            />
                                        </Avatar>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5em',
                                            alignItems: 'flex-start',
                                            flexGrow: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="l2dh7"
                                            fontWeight="500"
                                        >
                                            {offer.libelle}
                                        </Typography>
                                        <Chip
                                            label={getOfferTypeLabel(offer)}
                                            color="default"
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                backgroundColor: chipBg,
                                                color: chipText,
                                                fontSize: '12px',
                                                fontWeight: '500',
                                            }}
                                        />
                                    </Box>
                                    {isSelected && (
                                        <FaOptionIcon
                                            icon="check"
                                            sx={{ color: color.main }}
                                        />
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
};

export default SelectOfferContent;
